/**
 * 联机客户端：一条 WebSocket，20 Hz 上行，服务端只做转发。
 *
 * 协议刻意做到最薄（架构 §5.4）：同步表里没有「世界状态」，
 * 只有对方的姿态 + 座位。世界（海、岛、船）两端各自算，永远一致。
 */

export interface PoseMessage {
  x: number;
  z: number;
  yaw: number;
  seated: boolean;
}

export interface PeerInfo {
  id: string;
  name: string;
  avatar: string;
}

export type NetStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'full' | 'error';

const POSE_INTERVAL_MS = 50; // 20 Hz

export class NetClient {
  selfId = '';
  /** 已发出的姿态包数量（用来验证上行不依赖渲染循环） */
  sentPoses = 0;
  /** 进房时按「已有几个人」分配座位：0 号坐船尾左，1 号坐右 */
  joinSeat = 0;
  readonly peers = new Map<string, PeerInfo>();

  onPose: ((id: string, pose: PoseMessage) => void) | null = null;
  onExpr: ((id: string, name: string) => void) | null = null;
  onGesture: ((id: string, name: string) => void) | null = null;
  onChat: ((id: string, text: string) => void) | null = null;
  /** WebRTC 信令（rtc-offer / rtc-answer / rtc-ice） */
  onSignal: ((id: string, msg: Record<string, unknown>) => void) | null = null;
  /** 口型（0..1，10 Hz） */
  onMouth: ((id: string, value: number) => void) | null = null;
  onPeerJoin: ((peer: PeerInfo) => void) | null = null;
  onPeerLeave: ((id: string) => void) | null = null;
  onWelcome: (() => void) | null = null;
  onStatus: ((status: NetStatus, detail?: string) => void) | null = null;

  private ws: WebSocket | null = null;
  private lastSent = 0;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;

  get status(): NetStatus {
    return this.currentStatus;
  }

  private currentStatus: NetStatus = 'idle';

  connect(room: string, options: { name?: string; avatar?: string } = {}): void {
    this.closedByUser = false;
    this.setStatus('connecting');

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const params = new URLSearchParams({
      room,
      name: options.name ?? '旅人',
      avatar: options.avatar ?? '',
    });
    const ws = new WebSocket(`${proto}://${window.location.host}/ws?${params}`);
    this.ws = ws;

    ws.onopen = () => this.setStatus('open');

    ws.onclose = (ev) => {
      this.ws = null;
      if (this.closedByUser) {
        this.setStatus('closed');
        return;
      }
      // 409 = 房间满员，不要重连
      if (ev.code === 1008 || this.currentStatus === 'full') {
        this.setStatus('full');
        return;
      }
      this.setStatus('closed');
      this.retry = setTimeout(() => this.connect(room, options), 2000);
    };

    ws.onerror = () => this.setStatus('error');

    ws.onmessage = (ev) => {
      let msg: unknown;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      this.handle(msg as Record<string, unknown>);
    };
  }

  private handle(msg: Record<string, unknown>): void {
    switch (msg.t) {
      case 'welcome': {
        const self = msg.self as PeerInfo | undefined;
        this.selfId = self?.id ?? '';
        this.peers.clear();
        const incoming = (msg.peers as PeerInfo[] | undefined) ?? [];
        for (const p of incoming) {
          this.peers.set(p.id, p);
          this.onPeerJoin?.(p);
        }
        // 房间固定 2 席：先到坐 0 号，后到坐 1 号，否则两个人会叠在同一个位子上
        this.joinSeat = Math.min(1, incoming.length);
        this.onWelcome?.();
        break;
      }
      case 'peer-join': {
        const peer = msg.peer as PeerInfo;
        if (peer && peer.id !== this.selfId) {
          this.peers.set(peer.id, peer);
          this.onPeerJoin?.(peer);
        }
        break;
      }
      case 'peer-leave': {
        const id = String(msg.id ?? '');
        if (id) {
          this.peers.delete(id);
          this.onPeerLeave?.(id);
        }
        break;
      }
      case 'peer-msg': {
        const from = String(msg.from ?? '');
        let payload: unknown;
        try {
          payload = JSON.parse(String(msg.data ?? ''));
        } catch {
          return;
        }
        const data = payload as { t?: string; p?: PoseMessage; name?: string; text?: string; v?: number };
        if (!from) return;
        if (data.t === 'pose' && data.p) this.onPose?.(from, data.p);
        else if (data.t === 'expr' && data.name) this.onExpr?.(from, data.name);
        else if (data.t === 'gesture' && data.name) this.onGesture?.(from, data.name);
        else if (data.t === 'chat' && typeof data.text === 'string') this.onChat?.(from, data.text);
        else if (data.t === 'mouth' && typeof data.v === 'number') this.onMouth?.(from, data.v);
        else if (data.t?.startsWith('rtc-')) this.onSignal?.(from, data as Record<string, unknown>);
        break;
      }
      default:
        break;
    }
  }

  /** 20 Hz 节流上行。传 now 便于用同一个时钟源测试。 */
  sendPose(pose: PoseMessage, now = performance.now()): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (now - this.lastSent < POSE_INTERVAL_MS) return;
    this.lastSent = now;
    this.sentPoses++;
    this.ws.send(JSON.stringify({ t: 'pose', p: pose }));
  }

  /** 一次性表情：对方收到后自己按 duration 淡出 */
  sendExpr(name: string): void {
    this.send({ t: 'expr', name });
  }

  /** 手势（wave），一次性 */
  sendGesture(name: string): void {
    this.send({ t: 'gesture', name });
  }

  /** 文字聊天：截断 + 去掉换行，避免有人贴一屏东西 */
  sendChat(text: string): void {
    const clean = text.replace(/\s+/g, ' ').trim().slice(0, 120);
    if (!clean) return;
    this.send({ t: 'chat', text: clean });
  }

  /** 口型：10 Hz 上行一个 0..1 的开口度 */
  sendMouth(value: number): void {
    this.send({ t: 'mouth', v: Math.max(0, Math.min(1, value)) });
  }

  /** WebRTC 信令原样转发（SDP / ICE） */
  sendSignal(payload: unknown): void {
    this.send(payload);
  }

  private send(payload: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  close(): void {
    this.closedByUser = true;
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    this.ws?.close();
    this.ws = null;
  }

  private setStatus(status: NetStatus, detail?: string): void {
    this.currentStatus = status;
    this.onStatus?.(status, detail);
  }
}

/** 生成 6 位房码（去掉容易看错的 0/O/1/I） */
export function randomRoomCode(length = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  for (let i = 0; i < length; i++) out += alphabet[bytes[i]! % alphabet.length];
  return out;
}
